import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Topbar, Sidebar, BreadCrump, Loader } from '../../../components';
import {
    useLoan,
    useNpontuCustomerMandates,
    useNpontuMandateStatus,
    useNpontuMandateTransactions,
    useNpontuSuccessfulTransactions,
    useNpontuFailedTransactions,
} from '../../../hooks';
import { dateToHumanFormat } from '../../../utils/constants';

function InfoRow({ label, value }) {
    return (
        <div className="flex justify-between items-start py-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0 text-sm">
            <span className="text-gray-500 dark:text-gray-400 w-1/2">{label}</span>
            <span className="text-gray-800 dark:text-gray-200 font-medium w-1/2 text-right">{value ?? '—'}</span>
        </div>
    );
}

function SectionCard({ title, isLoading, children, className = '' }) {
    return (
        <div className={`border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm bg-white dark:bg-gray-800 flex flex-col ${className}`}>
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">{title}</h3>
            </div>
            <div className="px-5 py-4 flex-1">
                {isLoading ? (
                    <div className="flex justify-center py-4">
                        <Loader color="text-gray-400" size="w-5 h-5" />
                    </div>
                ) : (
                    children
                )}
            </div>
        </div>
    );
}

function EmptyState({ text }) {
    return <p className="text-sm text-gray-400 text-center py-4">{text}</p>;
}

function TxTable({ data, emptyText }) {
    if (!data?.length) return <EmptyState text={emptyText} />;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="text-xs text-gray-500 dark:text-gray-500 uppercase border-b dark:border-gray-700">
                        <th className="py-2 text-left font-medium">Amount (GHC)</th>
                        <th className="py-2 text-left font-medium">Created</th>
                        <th className="py-2 text-left font-medium">Updated</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((tx, i) => (
                        <tr key={tx?.id ?? i} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="py-2 text-gray-800 dark:text-gray-200 font-medium">{tx?.amount}</td>
                            <td className="py-2 text-gray-500 dark:text-gray-400">{dateToHumanFormat(tx?.created_at)}</td>
                            <td className="py-2 text-gray-500 dark:text-gray-400">{dateToHumanFormat(tx?.updated_at)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Details() {
    const { type, id } = useParams();

    const { isLoading: loanLoading, loan } = useLoan({
        loanId: id,
        loanType: type,
        state: null,
        approved: false,
        active: false,
        completed: false,
        rejected: false,
    });

    const phone = loan?.phone;
    const { isLoading: mandatesLoading, customerMandates } = useNpontuCustomerMandates({ phone });

    const [selectedMandate, setSelectedMandate] = useState(null);
    const activeMandate = selectedMandate ?? customerMandates?.[0];

    const { isLoading: mandateStatusLoading, mandateStatus } = useNpontuMandateStatus({
        ref: activeMandate?.third_party_reference_no,
    });

    const { isLoading: mandateTxLoading, mandateTransactions } = useNpontuMandateTransactions({
        mandateId: activeMandate?.id,
    });

    const { isLoading: successLoading, successfulTransactions } = useNpontuSuccessfulTransactions();
    const { isLoading: failedLoading, failedTransactions } = useNpontuFailedTransactions();

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-4">

                    <BreadCrump
                        path="/admin/npontu-clients"
                        main="Npontu Clients"
                        sub={loanLoading
                            ? <Loader color="text-gray-700" size="w-4 h-4" />
                            : `${loan?.client?.otherNames ?? ''} ${loan?.client?.surname ?? ''} — ${id}`
                        }
                    />

                    {/* Row 1 — Loan Details + Mandate Status */}
                    <div className="grid grid-cols-2 gap-4">

                        <SectionCard title="Loan Details" isLoading={loanLoading}>
                            <InfoRow label="Client Name" value={`${loan?.client?.otherNames ?? ''} ${loan?.client?.surname ?? ''}`.trim() || null} />
                            <InfoRow label="Phone" value={loan?.phone} />
                            <InfoRow label="Loan ID" value={loan?.loanId} />
                            <InfoRow label="Loan Type" value={loan?.type} />
                            <InfoRow label="Amount" value={loan?.amount ? `GHC ${loan.amount}` : null} />
                            <InfoRow label="Monthly Instalment" value={loan?.accountInfo ? `GHC ${loan.accountInfo?.premium}` : null} />
                            <InfoRow label="Duration" value={loan?.paymentDuration ? `${loan.paymentDuration} months` : null} />
                            <InfoRow label="State" value={loan?.state} />
                            <InfoRow label="Date Disbursed" value={loan?.dateDisbursed ? dateToHumanFormat(loan?.dateDisbursed) : dateToHumanFormat(loan?.startDate)} />
                        </SectionCard>

                        <SectionCard title="Mandate Status (ITC)" isLoading={mandateStatusLoading}>
                            {mandateStatus ? (
                                <>
                                    <InfoRow label="Mandate ID" value={mandateStatus?.id} />
                                    <InfoRow label="Status" value={mandateStatus?.transactions?.[0]?.status} />
                                    <InfoRow label="Reference" value={mandateStatus?.third_party_reference_no} />
                                    <InfoRow label="Last Updated" value={dateToHumanFormat(mandateStatus?.transactions?.[0]?.updated_at)} />
                                </>
                            ) : (
                                <EmptyState text={activeMandate ? "No status data available" : "No mandate to check status for"} />
                            )}
                        </SectionCard>

                    </div>

                    {/* Row 2 — Customer Mandates (full width compact table) */}
                    <SectionCard title={`Customer Mandates ${customerMandates?.length ? `(${customerMandates.length})` : ''}`} isLoading={mandatesLoading}>
                        {customerMandates?.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-xs text-gray-500 dark:text-gray-500 uppercase border-b dark:border-gray-700">
                                            <th className="py-2 text-left font-medium">Mandate ID</th>
                                            <th className="py-2 text-left font-medium">ITC Mandate ID</th>
                                            <th className="py-2 text-left font-medium">Reference</th>
                                            <th className="py-2 text-left font-medium">Network</th>
                                            <th className="py-2 text-left font-medium">Amount</th>
                                            <th className="py-2 text-left font-medium">Frequency</th>
                                            <th className="py-2 text-left font-medium">Start</th>
                                            <th className="py-2 text-left font-medium">End</th>
                                            <th className="py-2 text-left font-medium">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customerMandates.map((mandate, i) => (
                                            <tr key={mandate?.id ?? i} onClick={() => setSelectedMandate(mandate)} className={`border-b cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-500/10 ${activeMandate?.id === mandate?.id ? 'bg-blue-50 dark:bg-blue-500/10' : 'border-gray-50 dark:border-gray-700/50'}`}>
                                                <td className="py-2 text-gray-800 dark:text-gray-200 font-medium">{mandate?.id}</td>
                                                <td className="py-2 text-gray-500 dark:text-gray-400 font-mono text-xs">{mandate?.itc_mandate_id}</td>
                                                <td className="py-2 text-gray-500 dark:text-gray-400 font-mono text-xs">{mandate?.third_party_reference_no}</td>
                                                <td className="py-2 text-gray-800 dark:text-gray-200">{mandate?.network}</td>
                                                <td className="py-2 text-gray-800 dark:text-gray-200">GHC {mandate?.amount_to_debit}</td>
                                                <td className="py-2 text-gray-500 dark:text-gray-400 capitalize">{mandate?.frequency_type}</td>
                                                <td className="py-2 text-gray-500 dark:text-gray-400">{dateToHumanFormat(mandate?.start_date)}</td>
                                                <td className="py-2 text-gray-500 dark:text-gray-400">{dateToHumanFormat(mandate?.end_date)}</td>
                                                <td className="py-2">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${mandate?.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                                        {mandate?.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <EmptyState text="No mandates found for this customer" />
                        )}
                    </SectionCard>

                    {/* Row 3 — Successful + Failed Transactions */}
                    <div className="grid grid-cols-2 gap-4">

                        <SectionCard title="Successful Auto-Debit Transactions" isLoading={successLoading}>
                            <TxTable data={successfulTransactions} emptyText="No successful transactions found" />
                        </SectionCard>

                        <SectionCard title="Failed Auto-Debit Transactions" isLoading={failedLoading}>
                            <TxTable data={failedTransactions} emptyText="No failed transactions found" />
                        </SectionCard>

                    </div>

                    {/* Row 4 — Mandate Transactions (full width, only rendered if data exists) */}
                    {(mandateTxLoading || mandateTransactions?.length > 0) && (
                        <SectionCard title="Mandate Transactions" isLoading={mandateTxLoading}>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-xs text-gray-500 dark:text-gray-500 uppercase border-b dark:border-gray-700">
                                            <th className="py-2 text-left font-medium">Amount</th>
                                            <th className="py-2 text-left font-medium">Status</th>
                                            <th className="py-2 text-left font-medium">Created</th>
                                            <th className="py-2 text-left font-medium">Updated</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mandateTransactions?.map((tx, i) => (
                                            <tr key={tx?.id ?? i} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                                <td className="py-2 text-gray-800 dark:text-gray-200 font-medium">GHC {tx?.amount}</td>
                                                <td className="py-2">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tx?.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                                                        {tx?.status}
                                                    </span>
                                                </td>
                                                <td className="py-2 text-gray-500 dark:text-gray-400">{dateToHumanFormat(tx?.created_at)}</td>
                                                <td className="py-2 text-gray-500 dark:text-gray-400">{dateToHumanFormat(tx?.updated_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </SectionCard>
                    )}

                </section>
            </main>
        </div>
    );
}

export default Details;
